import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, Phone, Mail, MapPin, Clock, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../data/company';
import { QuoteForm } from '../components/common/QuoteForm';
import Seo from '../components/common/Seo';
import { routeMeta } from '../routeMeta';

const ContactPage = () => {
  const phoneNumbers = COMPANY_INFO.contact.phone || [];
  const primaryPhone = phoneNumbers[0];
  const mapsSearchUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${COMPANY_INFO.address.office}, ${COMPANY_INFO.address.city}, ${COMPANY_INFO.address.state}, ${COMPANY_INFO.address.country}`
  )}`;

  return (
    <div data-testid="contact-page">
      <Seo {...routeMeta['/contact']} path="/contact" />
      {/* Hero Section */}
      <section className="page-hero-shell tech-grid bg-[#0B1F3B] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <span>/</span>
              <span className="text-white">Contact</span>
            </div>
            <h1 className="font-['Manrope'] text-4xl md:text-6xl font-extrabold text-white uppercase tracking-tight">
              Get In Touch
            </h1>
            <p className="mt-6 text-xl text-slate-300 max-w-2xl mx-auto">
              Ready to discuss weighbridge, truck scale, or weighing scale requirements in Ghana, India, Africa, or Dubai? Our team is here to help you find the right solution.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="tech-grid bg-[#08152b] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 -mt-16 relative z-10">
            {[
              {
                icon: Phone,
                title: 'Phone',
                content: phoneNumbers,
                subContent: 'India',
              },
              {
                icon: Mail,
                title: 'Email',
                content: COMPANY_INFO.contact.email[0],
                subContent: 'Quotes and enquiries',
                href: `mailto:${COMPANY_INFO.contact.email[0]}`,
              },
              {
                icon: MapPin,
                title: 'Address',
                content: COMPANY_INFO.address.office,
                subContent: `${COMPANY_INFO.address.city}, ${COMPANY_INFO.address.state}`,
                href: '#map',
              },
              {
                icon: Clock,
                title: 'Business Hours',
                content: 'Mon - Sat: 9AM - 6PM',
                subContent: 'Sunday: Closed',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="premium-trust-card border border-white/10 bg-white/5 p-6 shadow-lg"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center bg-[#1E40AF]">
                  <item.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="mb-2 text-sm font-bold uppercase tracking-wider text-[#93C5FD]">
                  {item.title}
                </h3>
                {Array.isArray(item.content) ? (
                  <div className="space-y-1">
                    {item.content.map((phone) => (
                      <a key={phone} href={`tel:${phone}`} className="block font-medium text-white transition-colors hover:text-[#93C5FD]">
                        {phone}
                      </a>
                    ))}
                  </div>
                ) : item.href ? (
                  <a href={item.href} className="font-medium text-white transition-colors hover:text-[#93C5FD]">{item.content}</a>
                ) : (
                  <p className="font-medium text-white">{item.content}</p>
                )}
                <p className="mt-1 text-sm text-slate-300">{item.subContent}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="tech-grid bg-[#071327] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-eyebrow">Send a Message</span>
              <h2 className="font-['Manrope'] mt-4 text-3xl font-bold uppercase tracking-tight text-white md:text-4xl">
                Request a Quote
              </h2>
              <p className="mb-8 mt-4 text-slate-300">
                Fill out the form below and our engineering team will get back to you within 24 hours with guidance for your site, product, and installation requirements.
              </p>
              
              <div id="quote-form" className="premium-trust-card scroll-mt-32 border border-white/10 bg-white/5 p-8">
                <QuoteForm />
              </div>
            </motion.div>

            {/* Map & Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-eyebrow">Visit Us</span>
              <h2 className="font-['Manrope'] mt-4 text-3xl font-bold uppercase tracking-tight text-white md:text-4xl">
                Our Location
              </h2>
              <p className="mb-8 mt-4 text-slate-300">
                Visit our India office to see product demonstrations and discuss weighbridge, scale, indicator, and software requirements in person.
              </p>

              <div
                id="map"
                className="relative mb-8 overflow-hidden border border-white/10 bg-[#102548] p-6 text-white shadow-[0_24px_70px_rgba(2,8,23,0.32)] sm:p-8"
              >
                <div className="absolute inset-0 tech-grid opacity-35" aria-hidden="true" />
                <div className="relative">
                  <div className="mb-6 flex h-12 w-12 items-center justify-center bg-[#1E40AF]">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <h3 className="font-['Manrope'] text-2xl font-bold uppercase tracking-tight">
                    LEION Engineering Office
                  </h3>
                  <p className="mt-4 max-w-md text-sm leading-7 text-slate-300">
                    {COMPANY_INFO.address.office}, {COMPANY_INFO.address.city} - {COMPANY_INFO.address.pincode}, {COMPANY_INFO.address.state}
                  </p>
                  <a
                    href={mapsSearchUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 inline-flex min-h-12 items-center gap-2 border border-[#60A5FA] px-5 text-sm font-bold uppercase tracking-wider text-[#BFDBFE] transition-colors hover:bg-[#60A5FA] hover:text-[#0B1F3B]"
                  >
                    Open in Google Maps
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>

              {/* Address Details */}
              <div className="premium-trust-card bg-[#0B1F3B] p-8 text-white">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#93C5FD]">
                  <ShieldCheck className="h-4 w-4" />
                  Corporate Office
                </h3>
                <address className="not-italic space-y-3 text-slate-300">
                  <p className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#93C5FD]" />
                    <span>
                      {COMPANY_INFO.address.office}<br />
                      {COMPANY_INFO.address.city} - {COMPANY_INFO.address.pincode}<br />
                      {COMPANY_INFO.address.state}, {COMPANY_INFO.address.country}
                    </span>
                  </p>
                  <p className="flex items-start gap-3">
                    <Phone className="h-5 w-5 flex-shrink-0 text-[#93C5FD]" />
                    <span className="space-y-1">
                      {phoneNumbers.map((phone) => (
                        <a key={phone} href={`tel:${phone}`} className="block hover:text-white">
                          {phone}
                        </a>
                      ))}
                    </span>
                  </p>
                  <p className="flex items-center gap-3">
                    <Mail className="h-5 w-5 flex-shrink-0 text-[#93C5FD]" />
                    <a href={`mailto:${COMPANY_INFO.contact.email[0]}`} className="hover:text-white">
                      {COMPANY_INFO.contact.email[0]}
                    </a>
                  </p>
                </address>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="tech-grid bg-[#0B1F3B] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Need Immediate Assistance?
            </h3>
            <p className="text-white/80 mb-8">
              Call us directly for urgent product enquiries, technical support, or international weighing system requirements.
            </p>
            <a href={`tel:${primaryPhone}`}>
              <Button className="tech-shimmer rounded-sm bg-[#1E40AF] px-8 py-5 font-bold uppercase tracking-wider text-white shadow-[0_20px_50px_rgba(30,64,175,0.35)] hover:bg-[#2563EB]" data-testid="contact-call-btn">
                <Phone className="mr-2 h-5 w-5" />
                {primaryPhone}
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
